// src/components/ui/cards/PartnerRequestCard/PartnerRequestCardShadcn.jsx
import React from 'react';
import {
  Building2,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Check,
  X,
  Send,
  Clock,
  ArrowRight
} from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Button } from '../../button';
import { Badge } from '../../badge';
import { Avatar, AvatarFallback } from '../../avatar';

import { cn } from '../../../../utils/cn';

const PartnerRequestCardShadcn = ({
  request,
  type, // 'received' or 'sent'
  onAccept,
  onReject,
  onCancel,
  loading = false,
  className = ''
}) => {
  const isReceived = type === 'received';
  const isSent = type === 'sent';

  // Get business info based on request type
  const businessInfo = isReceived ? request.requesting_business : request.target_business;
  const businessName = businessInfo?.name || request.business_name || 'Unknown Business';
  const businessEmail = businessInfo?.contact_email || request.contact_email;
  const businessPhone = businessInfo?.contact_phone || request.contact_phone;

  // Format date
  const requestDate = request.requested_at || request.created_at;
  const formattedDate = requestDate
    ? new Date(requestDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : 'Recently';

  const handleAccept = (e) => {
    e.stopPropagation();
    if (onAccept && !loading) {
      onAccept();
    }
  };

  const handleReject = (e) => {
    e.stopPropagation();
    if (onReject && !loading) {
      onReject();
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    if (onCancel && !loading) {
      onCancel();
    }
  };

  return (
    <Card
      className={cn(
        "transition-all duration-200 hover:bg-accent/50",
        loading && "opacity-50 pointer-events-none",
        className
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Business Info */}
          <div className="flex items-start gap-4 flex-1">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                <Building2 className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-foreground">
                  {businessName}
                </h3>
                <Badge variant={isReceived ? "default" : "secondary"}>
                  {isReceived ? 'Incoming' : 'Outgoing'}
                </Badge>
              </div>

              {/* Contact Details */}
              <div className="space-y-2 mb-4">
                {businessEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{businessEmail}</span>
                  </div>
                )}

                {businessPhone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{businessPhone}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Requested {formattedDate}</span>
                </div>
              </div>

              {/* Request Note */}
              {request.notes && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      {isReceived ? 'Their message:' : 'Your message:'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground bg-muted rounded-lg p-3">
                    {request.notes}
                  </p>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-muted-foreground">
                  {isReceived ? 'Awaiting your response' : 'Awaiting their response'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 ml-4">
            {isReceived && (
              <>
                <Button onClick={handleAccept} disabled={loading} size="sm">
                  <Check className="w-4 h-4 mr-2" />
                  Accept
                </Button>
                <Button variant="outline" onClick={handleReject} disabled={loading} size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </>
            )}

            {isSent && (
              <Button variant="outline" onClick={handleCancel} disabled={loading} size="sm">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PartnerRequestCardShadcn;